import type { GeminiEnrichmentSettings, GeminiImageSettings } from '../types'

/**
 * Canonical catalog for every active Gemini model used by the image workflow.
 *
 * Keep model IDs, defaults, UI metadata, capabilities, and current prices here.
 * Historical rates belong in gemini-pricing.ts so retained usage rows continue
 * to calculate correctly after an active model is retired.
 */
export const GEMINI_IMAGE_ASPECT_RATIOS = [
  '1:1',
  '2:3',
  '3:2',
  '3:4',
  '4:3',
  '4:5',
  '5:4',
  '16:9',
  '9:16',
  '21:9'
] as const

export const GEMINI_IMAGE_SIZES = ['1K', '2K', '4K'] as const
export const GEMINI_THINKING_LEVELS = ['minimal', 'high'] as const

export type GeminiAspectRatio = (typeof GEMINI_IMAGE_ASPECT_RATIOS)[number]
export type GeminiImageSize = (typeof GEMINI_IMAGE_SIZES)[number]
export type GeminiThinkingLevel = (typeof GEMINI_THINKING_LEVELS)[number]

export interface GeminiModelPricing {
  /** USD per 1M input tokens. */
  inputPricePer1M: number
  /** USD per 1M output tokens. */
  outputPricePer1M: number
  /** USD per 1,000 Google Search queries, before account-wide free allowances. */
  googleSearchPricePer1KQueries?: number
  /** Flat USD charge for a generated image when the provider bills an image SKU. */
  imageOutputPrice?: number
  imageOutputPriceBySize?: Partial<Record<GeminiImageSize, number>>
}

type GeminiImageModelDefinition = {
  value: string
  label: string
  description: string
  shortDescription: string
  latency: 'Low' | 'Medium' | 'High'
  cost: 'Low' | 'Medium' | 'High'
  visualQuality: 'Low' | 'Medium' | 'High'
  reasoning: 'Low' | 'Medium' | 'High'
  supportedImageSizes: readonly GeminiImageSize[]
  supportsThinkingLevel: boolean
  supportsGoogleSearch: boolean
  pricing: GeminiModelPricing
}

type GeminiEnrichmentModelDefinition = {
  value: string
  label: string
  description: string
  pricing: GeminiModelPricing
}

export const GEMINI_IMAGE_MODELS = [
  {
    value: 'gemini-nano-banana-2.1',
    label: 'Nano Banana 2.1',
    description: 'Improved quality, text rendering, and consistency at Flash-level speed.',
    shortDescription: 'Balanced speed, sharp text and detail.',
    latency: 'Medium',
    cost: 'Medium',
    visualQuality: 'High',
    reasoning: 'Medium',
    supportedImageSizes: ['1K', '2K', '4K'],
    supportsThinkingLevel: true,
    supportsGoogleSearch: true,
    pricing: {
      inputPricePer1M: 1.5,
      outputPricePer1M: 7.5,
      googleSearchPricePer1KQueries: 14,
      imageOutputPrice: 0.0504,
      imageOutputPriceBySize: {
        '1K': 0.0336,
        '2K': 0.0504,
        '4K': 0.113
      }
    }
  },
  {
    value: 'gemini-3.1-flash-lite-image',
    label: 'Nano Banana 2 Lite',
    description: 'Fastest and lowest cost. Supports 1K output only.',
    shortDescription: 'Fastest, lower cost; 1K output only.',
    latency: 'Low',
    cost: 'Low',
    visualQuality: 'Medium',
    reasoning: 'Low',
    supportedImageSizes: ['1K'],
    supportsThinkingLevel: true,
    supportsGoogleSearch: false,
    pricing: {
      inputPricePer1M: 0.25,
      outputPricePer1M: 1.5,
      imageOutputPrice: 0.0336,
      imageOutputPriceBySize: { '1K': 0.0336 }
    }
  },
  {
    value: 'gemini-3.1-flash-image',
    label: 'Nano Banana 2',
    description: 'Default balanced model for high quality at moderate latency and cost.',
    shortDescription: 'Balanced speed and high image quality.',
    latency: 'Medium',
    cost: 'Medium',
    visualQuality: 'High',
    reasoning: 'Medium',
    supportedImageSizes: ['1K', '2K', '4K'],
    supportsThinkingLevel: true,
    supportsGoogleSearch: true,
    pricing: {
      inputPricePer1M: 0.5,
      outputPricePer1M: 3,
      googleSearchPricePer1KQueries: 14,
      imageOutputPrice: 0.101,
      imageOutputPriceBySize: {
        '1K': 0.067,
        '2K': 0.101,
        '4K': 0.151
      }
    }
  },
  {
    value: 'gemini-3-pro-image',
    label: 'Nano Banana Pro',
    description: 'Highest control and reasoning for complex professional image work.',
    shortDescription: 'Slower, with the most control and reasoning.',
    latency: 'High',
    cost: 'High',
    visualQuality: 'High',
    reasoning: 'High',
    supportedImageSizes: ['1K', '2K', '4K'],
    supportsThinkingLevel: false,
    supportsGoogleSearch: true,
    pricing: {
      inputPricePer1M: 2,
      outputPricePer1M: 12,
      googleSearchPricePer1KQueries: 14,
      imageOutputPrice: 0.134,
      imageOutputPriceBySize: {
        '1K': 0.134,
        '2K': 0.134,
        '4K': 0.24
      }
    }
  }
] as const satisfies readonly GeminiImageModelDefinition[]

export type GeminiImageModel = (typeof GEMINI_IMAGE_MODELS)[number]['value']

export const GEMINI_ENRICHMENT_MODELS = [
  {
    value: 'gemini-3.5-flash',
    label: 'Gemini 3.5 Flash',
    description: 'Default prompt enrichment model with stronger reasoning.',
    pricing: { inputPricePer1M: 1.5, outputPricePer1M: 9 }
  },
  {
    value: 'gemini-3.1-flash-lite',
    label: 'Gemini 3.1 Flash-Lite',
    description: 'Lower-cost enrichment model for simpler prompt cleanup.',
    pricing: { inputPricePer1M: 0.25, outputPricePer1M: 1.5 }
  }
] as const satisfies readonly GeminiEnrichmentModelDefinition[]

export type GeminiEnrichmentModel = (typeof GEMINI_ENRICHMENT_MODELS)[number]['value']

export const DEFAULT_GEMINI_IMAGE_MODEL = 'gemini-3.1-flash-image' satisfies GeminiImageModel
/** @deprecated Use DEFAULT_GEMINI_IMAGE_MODEL. */
export const GEMINI_IMAGE_MODEL = DEFAULT_GEMINI_IMAGE_MODEL
export const DEFAULT_GEMINI_ENRICHMENT_MODEL = 'gemini-3.5-flash' satisfies GeminiEnrichmentModel
/** @deprecated Use DEFAULT_GEMINI_ENRICHMENT_MODEL. */
export const GEMINI_MULTIMODAL_MODEL = DEFAULT_GEMINI_ENRICHMENT_MODEL

function modelValues<T extends readonly { value: string }[]>(
  models: T
): readonly [T[number]['value'], ...T[number]['value'][]] {
  const [first, ...remaining] = models.map(({ value }) => value)
  if (!first) throw new Error('Gemini model manifest must contain at least one model.')
  return [first, ...remaining] as readonly [T[number]['value'], ...T[number]['value'][]]
}

export const GEMINI_IMAGE_MODEL_VALUES = modelValues(GEMINI_IMAGE_MODELS)
export const GEMINI_ENRICHMENT_MODEL_VALUES = modelValues(GEMINI_ENRICHMENT_MODELS)

export const GEMINI_IMAGE_MODEL_OPTIONS = GEMINI_IMAGE_MODELS.map(({ value, label, description }) => ({
  value,
  label,
  description
}))

export const GEMINI_ENRICHMENT_MODEL_OPTIONS = GEMINI_ENRICHMENT_MODELS.map(({ value, label, description }) => ({
  value,
  label,
  description
}))

/** Historical tool defaults; omitted Outpaint/Upscale model fields use these values. */
export const GEMINI_FIXED_IMAGE_TOOL_MODELS = {
  outpaint: 'gemini-3.1-flash-image',
  upscale: 'gemini-3.1-flash-image'
} as const satisfies Record<'outpaint' | 'upscale', GeminiImageModel>

export const GEMINI_IMAGE_TEMPERATURE_MIN = 0
export const GEMINI_IMAGE_TEMPERATURE_MAX = 2
export const GEMINI_IMAGE_TEMPERATURE_STEP = 0.1

export const DEFAULT_GEMINI_ENRICHMENT_SETTINGS = {
  model: DEFAULT_GEMINI_ENRICHMENT_MODEL
} as const satisfies GeminiEnrichmentSettings

export const DEFAULT_GEMINI_IMAGE_SETTINGS = {
  model: DEFAULT_GEMINI_IMAGE_MODEL,
  temperature: 0.6,
  aspectRatio: '1:1',
  imageSize: '2K',
  thinkingLevel: 'minimal'
} as const satisfies GeminiImageSettings

export const GEMINI_IMAGE_CONFIG = {
  temperature: DEFAULT_GEMINI_IMAGE_SETTINGS.temperature,
  responseModalities: ['IMAGE'],
  imageConfig: {
    aspectRatio: DEFAULT_GEMINI_IMAGE_SETTINGS.aspectRatio,
    imageSize: DEFAULT_GEMINI_IMAGE_SETTINGS.imageSize
  },
  thinkingConfig: { thinkingLevel: DEFAULT_GEMINI_IMAGE_SETTINGS.thinkingLevel }
} as const

export function getGeminiImageModel(model: GeminiImageModel) {
  return GEMINI_IMAGE_MODELS.find((entry) => entry.value === model)
}

export function getGeminiEnrichmentModel(model: GeminiEnrichmentModel) {
  return GEMINI_ENRICHMENT_MODELS.find((entry) => entry.value === model)
}

export function getSupportedGeminiImageSizes(model: GeminiImageModel): readonly GeminiImageSize[] {
  return getGeminiImageModel(model)?.supportedImageSizes ?? GEMINI_IMAGE_SIZES
}

export function isGeminiImageSizeSupported(model: GeminiImageModel, imageSize: GeminiImageSize): boolean {
  return getSupportedGeminiImageSizes(model).includes(imageSize)
}

export function supportsGeminiThinkingLevel(model: GeminiImageModel): boolean {
  return getGeminiImageModel(model)?.supportsThinkingLevel ?? false
}

export function supportsGeminiGoogleSearch(model: GeminiImageModel): boolean {
  return getGeminiImageModel(model)?.supportsGoogleSearch ?? false
}

export const GEMINI_ACTIVE_PRICING = Object.fromEntries(
  [...GEMINI_IMAGE_MODELS, ...GEMINI_ENRICHMENT_MODELS].map(({ value, pricing }) => [value, pricing])
) as Readonly<Record<GeminiImageModel | GeminiEnrichmentModel, GeminiModelPricing>>
